import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Brand, LangToggle, applyDocLang, detectLang, saveLang } from "@/components/brand";
import { CONTACT_EMAIL } from "@/lib/site";
import { useSeo } from "@/lib/seo";

const COPY = {
  tr: {
    title: "Gizlilik ve KVKK aydınlatma metni",
    updated: "Son güncelleme: Ekim 2026",
    back: "Ana sayfa",
    sections: [
      ["Kim olduğumuz", "RateGuard, otellerin misafir geri bildirimlerini toplamasını sağlayan bir yazılımdır. Bu metin, 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) kapsamında hangi verileri neden işlediğimizi açıklar."],
      ["Misafirlerden topladığımız veriler", "Puan (1–5), isteğe bağlı olarak yazılan mesaj, oda numarası ve gönderim zamanı. Misafirden ad, telefon, e-posta veya kimlik bilgisi istemeyiz. Misafir mesajına kendi isteğiyle kişisel bilgi yazarsa bu bilgi yalnızca ilgili otelle paylaşılır."],
      ["Bu verileri neden işliyoruz", "Misafirin konaklaması sırasında yaşadığı sorunun otel tarafından hızla çözülmesi ve otelin hizmet kalitesini ölçmesi için. Veriler yalnızca misafirin konakladığı otelle paylaşılır; başka otellerle, reklam şirketleriyle veya üçüncü taraflarla paylaşılmaz ve satılmaz."],
      ["Demo formu", "Ad soyad, otel adı, şehir, oda sayısı, telefon ve e-posta bilgilerini yalnızca demo planlamak ve sizinle iletişime geçmek için açık rızanızla işleriz."],
      ["Saklama süresi", "Geri bildirimler, otel aboneliği süresince ve otel silinene kadar saklanır. Otel hesabı silindiğinde ilgili tüm geri bildirimler kalıcı olarak silinir. Demo talepleri en fazla 12 ay saklanır."],
      ["Haklarınız", "KVKK'nın 11. maddesi kapsamında verilerinize erişme, düzeltilmesini veya silinmesini isteme ve işlemeye itiraz etme haklarına sahipsiniz. Talepleriniz için bize aşağıdaki adresten yazabilirsiniz."],
    ],
    contact: "İletişim",
    noContact: "İletişim için ana sayfadaki formu kullanabilirsiniz.",
  },
  en: {
    title: "Privacy notice",
    updated: "Last updated: October 2026",
    back: "Home",
    sections: [
      ["Who we are", "RateGuard is software that helps hotels collect guest feedback. This notice explains what data we process and why."],
      ["Data we collect from guests", "A rating (1–5), an optional message, the room number and the time of submission. We never ask guests for their name, phone, email or ID. If a guest chooses to write personal details in a message, it is shared only with that hotel."],
      ["Why we process it", "So the hotel can fix a guest's problem during their stay and measure its service quality. Data is shared only with the hotel the guest stayed at. It is never shared with other hotels, advertisers or third parties, and never sold."],
      ["Demo form", "We use your name, hotel name, city, room count, phone and email only to arrange a demo and contact you, based on your consent."],
      ["Retention", "Feedback is kept for as long as the hotel's account exists. When a hotel account is deleted, all of its feedback is permanently deleted. Demo requests are kept for up to 12 months."],
      ["Your rights", "You can ask to access, correct or delete your data, or object to its processing. Write to us at the address below."],
    ],
    contact: "Contact",
    noContact: "Please use the form on our home page to contact us.",
  },
  sr: {
    title: "Obaveštenje o privatnosti",
    updated: "Poslednje ažuriranje: oktobar 2026.",
    back: "Početna",
    sections: [
      ["Ko smo mi", "RateGuard je softver koji hotelima pomaže da prikupljaju utiske gostiju. Ovo obaveštenje objašnjava koje podatke obrađujemo i zašto."],
      ["Podaci koje prikupljamo od gostiju", "Ocena (1–5), opciona poruka, broj sobe i vreme slanja. Od gostiju nikada ne tražimo ime, telefon, e-poštu ili lični dokument. Ako gost sam upiše lične podatke u poruku, oni se dele samo sa tim hotelom."],
      ["Zašto ih obrađujemo", "Da bi hotel rešio problem gosta tokom boravka i merio kvalitet usluge. Podaci se dele samo sa hotelom u kojem je gost boravio. Nikada se ne dele sa drugim hotelima, oglašivačima ili trećim licima i nikada se ne prodaju."],
      ["Formular za demo", "Ime, naziv hotela, grad, broj soba, telefon i e-poštu koristimo samo za zakazivanje demoa i kontakt sa vama, na osnovu vaše saglasnosti."],
      ["Čuvanje podataka", "Utisci se čuvaju dok postoji nalog hotela. Kada se nalog hotela obriše, svi njegovi utisci se trajno brišu. Zahtevi za demo čuvaju se najduže 12 meseci."],
      ["Vaša prava", "Možete zatražiti pristup, ispravku ili brisanje svojih podataka, kao i uložiti prigovor na obradu. Pišite nam na adresu ispod."],
    ],
    contact: "Kontakt",
    noContact: "Za kontakt koristite formular na početnoj stranici.",
  },
};

const initialLang = () => {
  try {
    const q = new URLSearchParams(window.location.search).get("lang");
    if (q && COPY[q]) return q;
  } catch (e) {
    /* ignore */
  }
  /* The URL decides for search engines: /gizlilik is Turkish, /privacy English. */
  if (window.location.pathname.startsWith("/gizlilik")) return "tr";
  if (window.location.pathname.startsWith("/privacy")) return "en";
  return detectLang();
};

export const Privacy = () => {
  const [lang, setLang] = useState(initialLang);
  const c = COPY[lang] || COPY.tr;
  useEffect(() => applyDocLang(lang), [lang]);
  useSeo({
    title: lang === "tr" ? "Gizlilik ve KVKK · RateGuard" : lang === "sr" ? "Privatnost · RateGuard" : "Privacy · RateGuard",
    description: lang === "tr" ? "RateGuard misafir verisini nasıl işler: yalnızca puan, mesaj ve oda numarası saklanır." : "How RateGuard handles guest data: only the rating, message and room number are stored.",
    path: lang === "tr" ? "/gizlilik" : "/privacy",
  });

  return (
    <div className="scene" style={{ minHeight: "100dvh" }}>
      <header className="wrap flex items-center justify-between gap-3 pt-6">
        <Brand to={lang === "tr" ? "/" : `/${lang}`} />
        <LangToggle
          lang={lang}
          onChange={(code) => {
            saveLang(code);
            setLang(code);
          }}
        />
      </header>
      <main className="wrap" style={{ paddingBlock: "3rem 4rem", maxWidth: 820 }}>
        <div className="eyebrow">{c.updated}</div>
        <h1 className="display mt-3" style={{ fontSize: "clamp(2.2rem, 6vw, 3.4rem)" }}>{c.title}</h1>
        <div className="panel mt-8" style={{ padding: "clamp(1.4rem, 4vw, 2.2rem)" }}>
          <div className="grid gap-7">
            {c.sections.map(([h, p]) => (
              <section key={h}>
                <h2 className="font-bold" style={{ fontSize: "1.05rem" }}>{h}</h2>
                <p className="muted mt-2" style={{ margin: "0.5rem 0 0", maxWidth: "68ch" }}>{p}</p>
              </section>
            ))}
            <section>
              <h2 className="font-bold" style={{ fontSize: "1.05rem" }}>{c.contact}</h2>
              <p className="muted mt-2" style={{ margin: "0.5rem 0 0" }}>
                {CONTACT_EMAIL ? <a href={`mailto:${CONTACT_EMAIL}`} className="mono underline underline-offset-4">{CONTACT_EMAIL}</a> : c.noContact}
              </p>
            </section>
          </div>
        </div>
        <Link to={lang === "tr" ? "/" : `/${lang}`} className="btn btn-secondary btn-sm mt-8">← {c.back}</Link>
      </main>
    </div>
  );
};

export default Privacy;
